import { cn } from '@/lib/utils';
import Chip from '@/components/ui/Chip.tsx';
import NoItems from '@/components/ui/NoItems.tsx';
import { SettlementOptionVm } from '@/features/dashboard/bookingChanges/types/ChangePackageFlowViewModel.ts';
import { SettlementDistribution } from '@/features/dashboard/bookingChanges/types/SettlementDistribution.ts';
import { SETTLEMENT_UNAVAILABLE_LABEL } from '@/features/dashboard/bookingChanges/types/SettlementDistribution.utils.ts';
import {
  RadioCard,
  RadioDot,
  RadioGroup,
} from '@/components/ui/radio-group.tsx';

interface SettlementOptionListProps
  extends Omit<
    React.ComponentPropsWithoutRef<typeof RadioGroup>,
    'value' | 'onValueChange' | 'onChange'
  > {
  options: SettlementOptionVm[];
  value: SettlementDistribution;
  onChange: (option: SettlementDistribution) => void;
}

/**
 * Lewa kolumna kroku W2 — rozkład dopłaty. Backend zna jeden rozkład, więc
 * wybieralna jest jedna karta; pozostałe stoją wyszarzone z dopiskiem, żeby
 * operator wiedział, czego może się spodziewać, a nie zgadywał, czemu zniknęły.
 */
const SettlementOptionList = ({
  options,
  value,
  onChange,
  className,
  ...props
}: SettlementOptionListProps) => {
  if (options.length === 0) {
    return (
      <div className={cn('py-2', className)} {...props}>
        <NoItems
          title="Brak wariantów rozliczenia"
          description="Nie udało się przeliczyć, jak dopłata może wejść w harmonogram rat."
        />
      </div>
    );
  }

  return (
    <RadioGroup
      aria-label="Rozkład dopłaty"
      value={value}
      onValueChange={(next) => onChange(next as SettlementDistribution)}
      className={className}
      {...props}
    >
      {options.map((option) => (
        <RadioCard
          key={option.option}
          value={option.option}
          disabled={!option.available}
          className={cn(
            'flex items-start gap-3 border border-neutral-200 bg-basic-white px-4 py-3.5',
            'data-[state=unchecked]:hover:border-neutral-300',
            'data-[state=checked]:border-accent-border data-[state=checked]:bg-accent-100',
            !option.available &&
              'cursor-not-allowed bg-neutral-50 opacity-60 data-[state=unchecked]:hover:border-neutral-200',
          )}
        >
          <RadioDot className="mt-0.5" />
          <span className="min-w-0">
            <span className="flex flex-wrap items-center gap-2">
              <span className="text-[15px] font-bold text-ink">
                {option.title}
              </span>
              {!option.available && (
                <Chip
                  label={SETTLEMENT_UNAVAILABLE_LABEL}
                  tone="neutral"
                  fill="soft"
                />
              )}
            </span>
            <span className="mt-[3px] block text-sm text-neutral-500">
              {option.description}
            </span>
          </span>
        </RadioCard>
      ))}
    </RadioGroup>
  );
};

export default SettlementOptionList;
